import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { market } from "@/api/market";
import { useOneClickOrder } from "@/hooks/useOneClickOrder";
import { useWsSubscribe } from "@/ws/WebSocketProvider";
import type { WatchSymbol } from "./WatchlistPane";

/**
 * Order book pane with one-click trading.
 *
 * Polls `GET /piggy/api/depth/{symbol}` every 1s — the backend returns
 * real `internal_limit_orders` levels from the L2 matcher when resting
 * liquidity exists, and synthesizes plausible levels around the live
 * BBO otherwise. Live `client_tick` / `tick` events trigger an
 * immediate refetch so the book follows the spread without waiting
 * for the next poll.
 *
 * One-click trading: clicking an ask cell submits a BUY market;
 * clicking a bid cell submits a SELL market. Shift-click on either
 * submits a LIMIT order at the clicked price (BUY on bid side,
 * SELL on ask side — joining the queue rather than crossing it).
 * Quantity defaults to `prefs.oneclick_default_qty`. A confirm
 * dialog gates the submission unless `prefs.oneclick_confirm=false`.
 */
interface Level {
  price: number;
  size: number;
}

export function BookPane({ symbol }: { symbol: WatchSymbol }) {
  const [bids, setBids] = useState<Level[]>([]);
  const [asks, setAsks] = useState<Level[]>([]);
  const [last, setLast] = useState<number | null>(null);
  const oneClick = useOneClickOrder();

  useEffect(() => {
    setBids([]); setAsks([]); setLast(null);
  }, [symbol.symbol]);

  const depthQ = useQuery({
    queryKey: ["depth", symbol.symbol],
    queryFn: () => market.depth(symbol.symbol, 10),
    refetchInterval: 1_000,
  });

  useEffect(() => {
    if (!depthQ.data) return;
    setBids(depthQ.data.bids.map(([p, s]) => ({ price: Number(p), size: Number(s) })));
    setAsks(depthQ.data.asks.map(([p, s]) => ({ price: Number(p), size: Number(s) })));
    if (depthQ.data.mid != null) setLast(depthQ.data.mid);
  }, [depthQ.data]);

  // Live tick events nudge a refetch so the book follows the spread.
  function onTick(msg: Record<string, unknown>) {
    if (msg.symbol !== symbol.symbol) return;
    depthQ.refetch();
  }
  useWsSubscribe("client_tick", onTick);
  useWsSubscribe("tick", onTick);

  const maxSize = Math.max(
    1,
    ...bids.map((l) => l.size),
    ...asks.map((l) => l.size),
  );
  const precision = symbol.symbol.includes("/")
    ? symbol.symbol.endsWith("JPY") ? 3 : 5
    : 2;

  function onCellClick(price: number, side: "bid" | "ask", isLimit: boolean) {
    // Plain click: market in the OPPOSITE direction (cross the spread).
    //   click ask  → BUY market
    //   click bid  → SELL market
    // Shift-click: limit on the SAME side at that price (join the queue).
    //   shift+click bid → BUY limit @ that bid
    //   shift+click ask → SELL limit @ that ask
    const orderSide: "BUY" | "SELL" = isLimit
      ? (side === "bid" ? "BUY" : "SELL")
      : (side === "ask" ? "BUY" : "SELL");
    oneClick.fire({
      symbol: symbol.symbol,
      asset_class: symbol.asset_class,
      side: orderSide,
      order_type: isLimit ? "LIMIT" : "MARKET",
      price: isLimit ? price : undefined,
    });
  }

  return (
    <div className="flex h-full flex-col text-2xs">
      <div className="flex items-baseline border-b border-term-border bg-term-bg px-2 py-1 text-term-mute">
        <div className="flex-1 uppercase tracking-wider">Price</div>
        <div className="w-16 text-right uppercase tracking-wider">Size</div>
        <div
          className="ml-2 text-2xs uppercase tracking-wider"
          title={
            oneClick.confirmOn
              ? "Click confirms before sending. Disable in Preferences for true 1-click."
              : "1-click is ON. Click an ask to BUY market, click a bid to SELL market. Shift-click for LIMIT at price."
          }
        >
          {oneClick.confirmOn ? "click→confirm" : "1-click"}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {/* Asks descending so the lowest sits next to the spread row. */}
        {asks.slice().reverse().map((l, i) => (
          <BookRow
            key={`a-${i}`}
            level={l}
            maxSize={maxSize}
            side="ask"
            precision={precision}
            onCellClick={onCellClick}
            disabled={oneClick.pending}
          />
        ))}
        {last !== null && (
          <div className="flex border-y border-term-border-strong bg-term-elev px-2 py-1 font-semibold">
            <div className="flex-1 tnum">{last.toFixed(precision)}</div>
            <div className="text-2xs uppercase tracking-wider text-term-mute">mid</div>
          </div>
        )}
        {bids.map((l, i) => (
          <BookRow
            key={`b-${i}`}
            level={l}
            maxSize={maxSize}
            side="bid"
            precision={precision}
            onCellClick={onCellClick}
            disabled={oneClick.pending}
          />
        ))}
      </div>
    </div>
  );
}

function BookRow({
  level,
  maxSize,
  side,
  precision,
  onCellClick,
  disabled,
}: {
  level: Level;
  maxSize: number;
  side: "bid" | "ask";
  precision: number;
  onCellClick: (price: number, side: "bid" | "ask", isLimit: boolean) => void;
  disabled: boolean;
}) {
  const pct = (level.size / maxSize) * 100;
  const tone = side === "bid" ? "up" : "down";
  const bg = side === "bid" ? "bg-emerald-900/25" : "bg-red-900/25";
  const hoverHint = side === "bid"
    ? "Click to SELL market. Shift-click to BUY limit @ this price."
    : "Click to BUY market. Shift-click to SELL limit @ this price.";
  return (
    <button
      type="button"
      title={hoverHint}
      disabled={disabled}
      onClick={(e) => onCellClick(level.price, side, e.shiftKey)}
      className="relative flex w-full px-2 py-0.5 text-left hover:bg-term-elev disabled:opacity-50 disabled:cursor-wait"
    >
      <div
        className={`absolute inset-y-0 ${side === "bid" ? "left-0" : "right-0"} ${bg}`}
        style={{ width: `${pct}%` }}
      />
      <div className={`relative flex-1 tnum ${tone}`}>{level.price.toFixed(precision)}</div>
      <div className="relative w-16 text-right tnum text-term-dim">{level.size}</div>
    </button>
  );
}
